import type { MediaGalleryItem } from '../../MediaGallery.tsx';
import styles from './CarouselSlide.module.scss';
import VideoPlayer from '../../../VideoPlayer/VideoPlayer.tsx';
import { lastEventSource } from '@/widgets/hooks/useVideoTracking.tsx';
import {
  trackVideoPause,
  trackVideoPlay
} from '@/widgets/ProductDetailsHero/Tracking.ts';
import useEffectOnChange from '@/widgets/hooks/useEffectOnChange.tsx';

export type CarouselSlideProps = {
  item: MediaGalleryItem;
  isCurrentSlide: boolean;
  loading?: HTMLImageElement['loading'];
  a11yLabel: string;
  noVideoSupportLabel: string;
  playPauseButtonLabel: string;
  hasReducedMotion: boolean;
  loopVideos?: boolean;
  carouselInFold?: boolean;
};

export default function CarouselSlide({
  item,
  isCurrentSlide,
  loading = undefined,
  a11yLabel,
  noVideoSupportLabel,
  playPauseButtonLabel,
  hasReducedMotion,
  loopVideos,
  carouselInFold = true
}: CarouselSlideProps) {
  const imageSrc = item.type === 'VIDEO' ? item.poster : item.url;
  const videoSrc = item.type === 'VIDEO' ? item.url : undefined;
  const videoPoster = item.type === 'VIDEO' ? item.poster : undefined;
  const videoCanPlay = isCurrentSlide && carouselInFold;

  useEffectOnChange(() => {
    lastEventSource.value = 'automatic';
  }, [carouselInFold]);

  return (
    <li class={styles.listItem}>
      {item.type === 'IMAGE' && imageSrc && (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <figure class={styles.slideClickable} aria-label={a11yLabel}>
          <CarouselSlide.Image
            src={imageSrc}
            alt={item.alt}
            width={800}
            height={800}
            loading={loading}
          />
        </figure>
      )}
      {item.type === 'VIDEO' && videoSrc && (
        <CarouselSlide.Video
          url={videoSrc}
          poster={videoPoster}
          noVideoSupportLabel={noVideoSupportLabel}
          playPauseButtonLabel={playPauseButtonLabel}
          videoCanPlay={videoCanPlay}
          loopVideos={loopVideos}
          hasReducedMotion={hasReducedMotion}
        />
      )}
    </li>
  );
}

CarouselSlide.Image = Image;
CarouselSlide.Video = Video;

type ImageProps = {
  src: string;
  alt: string;
  width: number;
  height: number;
  loading?: HTMLImageElement['loading'];
};
function Image({ src, alt, width, height, loading }: ImageProps) {
  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      class={styles.carouselSlide}
      draggable={false}
      loading={loading}
      decoding="async"
    />
  );
}

type VideoProps = {
  url: string;
  poster?: string;
  noVideoSupportLabel: string;
  playPauseButtonLabel: string;
  videoCanPlay: boolean;
  loopVideos?: boolean;
  hasReducedMotion: boolean;
};
function Video({
  url,
  poster,
  noVideoSupportLabel,
  playPauseButtonLabel,
  videoCanPlay,
  loopVideos,
  hasReducedMotion
}: VideoProps) {
  useEffectOnChange(() => {
    if (videoCanPlay) {
      trackVideoPlay(url, lastEventSource.value);
    } else {
      trackVideoPause(url, lastEventSource.value);
    }
  }, [videoCanPlay]);

  return (
    <div class={styles.videoSlide}>
      <VideoPlayer
        source={[
          {
            url,
            type: 'video/mp4'
          }
        ]}
        poster={poster}
        translations={{
          noVideoSupportLabel,
          playPauseButtonLabel
        }}
        canPlay={videoCanPlay}
        loop={loopVideos}
        hasReducedMotion={hasReducedMotion}
      />
    </div>
  );
}
